/** Contratos de pagos, recibos y anulaciones. */

import { z } from "zod";
import {
  idSchema,
  instantSchema,
  moneySchema,
  operationalDateSchema,
  positiveMoneySchema,
} from "./common.js";

export const paymentMethodSchema = z.enum([
  "EFECTIVO",
  "TRANSFERENCIA",
  "DEPOSITO",
  "CHEQUE",
  "SALDO_A_FAVOR",
]);

export const paymentStatusSchema = z.enum([
  "PENDIENTE",
  "CONFIRMADO",
  "RECHAZADO",
  "EN_CONFLICTO",
  "ANULADO",
]);

export const allocationInputSchema = z.object({
  chargeId: idSchema,
  amount: positiveMoneySchema,
});

/**
 * Peticion de registro de pago.
 *
 * `paymentId` lo genera el DISPOSITIVO antes de guardar localmente. Es la pieza
 * clave de la idempotencia: reenviar la misma peticion N veces produce siempre el
 * mismo pago, nunca duplicados.
 */
export const createPaymentRequestSchema = z.object({
  paymentId: idSchema,
  studentId: idSchema,
  cycleId: idSchema,
  amount: positiveMoneySchema,
  method: paymentMethodSchema,
  reference: z.string().trim().max(40).nullable().default(null),
  allocations: z.array(allocationInputSchema).min(1).max(50),
  effectiveAt: instantSchema,
  capturedAt: instantSchema,
  operationalDate: operationalDateSchema,
  installationId: idSchema,
  note: z.string().trim().max(500).nullable().default(null),
  /**
   * Descuento por pagar la colegiatura del año completa.
   *
   * Viaja DENTRO del pago y no como una operación aparte porque la caja cobra
   * sin conexión: el descuento y el dinero tienen que llegar juntos al servidor
   * y aplicarse o fallar juntos. Si fueran dos pasos, una caída entre medias
   * dejaría al alumno rebajado y sin pagar, o pagado y sin rebajar.
   *
   * El servidor comprueba la condición —que el año quede cubierto— y reparte la
   * rebaja entre las mensualidades. No se fía del importe que mande el
   * dispositivo: solo del porcentaje.
   */
  annualDiscount: z
    .object({ percent: z.number().int().positive().max(50) })
    .nullable()
    .default(null),
  /**
   * Version del estudiante que el dispositivo tenia al capturar. Habilita el control
   * de concurrencia optimista: si cambio en el servidor, se evalua conflicto.
   */
  expectedStudentVersion: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .default(null),
});
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>;

export const paymentAllocationSchema = z.object({
  chargeId: idSchema,
  conceptId: idSchema,
  conceptName: z.string(),
  amount: moneySchema,
  /** Parte de la línea que cubrió mora. Cero en conceptos sin mora. */
  lateFee: moneySchema.nullable().default(null),
  /** Parte de la línea que cubrió capital. */
  capital: moneySchema.nullable().default(null),
  cycleId: idSchema.nullable().default(null),
  cycleName: z.string().nullable().default(null),
  /** Mes 1-12 si es una mensualidad. */
  month: z.number().int().min(1).max(12).nullable().default(null),
  balanceBefore: moneySchema,
  balanceAfter: moneySchema,
});

export const paymentSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  studentId: idSchema,
  studentCode: z.string(),
  studentName: z.string(),
  cycleId: idSchema,
  /** Grado y seccion CONGELADOS al momento de la captura (no cambian con el tiempo). */
  gradeName: z.string(),
  sectionName: z.string(),
  amount: moneySchema,
  method: paymentMethodSchema,
  reference: z.string().nullable(),
  allocations: z.array(paymentAllocationSchema),
  creditAmount: moneySchema,
  status: paymentStatusSchema,
  effectiveAt: instantSchema,
  capturedAt: instantSchema,
  confirmedAt: instantSchema.nullable(),
  operationalDate: operationalDateSchema,
  userId: idSchema,
  userName: z.string(),
  installationId: idSchema,
  note: z.string().nullable(),
  studentBalanceBefore: moneySchema,
  studentBalanceAfter: moneySchema,
  /** Consecutivo oficial asignado centralmente. `null` mientras el pago sea provisional. */
  receiptNumber: z.string().nullable(),
  voidedByPaymentId: idSchema.nullable(),
  version: z.number().int(),
});
export type PaymentDto = z.infer<typeof paymentSchema>;

export const createPaymentResponseSchema = z.object({
  payment: paymentSchema,
  /** `true` si esta peticion no creo nada porque la clave idempotente ya existia. */
  idempotentReplay: z.boolean(),
});
export type CreatePaymentResponse = z.infer<typeof createPaymentResponseSchema>;

export const voidPaymentRequestSchema = z.object({
  voidId: idSchema,
  reason: z.string().trim().min(5).max(500),
  installationId: idSchema,
  at: instantSchema,
  operationalDate: operationalDateSchema,
});
export type VoidPaymentRequest = z.infer<typeof voidPaymentRequestSchema>;

export const paymentSearchQuerySchema = z.object({
  studentId: idSchema.optional(),
  cycleId: idSchema.optional(),
  gradeId: idSchema.optional(),
  sectionId: idSchema.optional(),
  conceptId: idSchema.optional(),
  userId: idSchema.optional(),
  method: paymentMethodSchema.optional(),
  status: paymentStatusSchema.optional(),
  from: operationalDateSchema.optional(),
  to: operationalDateSchema.optional(),
});
export type PaymentSearchQuery = z.infer<typeof paymentSearchQuerySchema>;

// ---------------------------------------------------------------------------
// Recibos
// ---------------------------------------------------------------------------

/**
 * Datos inmutables del recibo. La PWA genera el PDF provisional con exactamente
 * esta estructura offline, y la API regenera el confirmado desde los mismos campos
 * persistidos: el documento sale identico salvo el estado y el consecutivo.
 */
/**
 * Piezas del comprobante.
 *
 * Se declaran aparte porque las usan DOS documentos: el recibo de un pago y el
 * comprobante consolidado de varios. Duplicadas, tarde o temprano una ganaría un
 * campo que la otra no, y el mismo pago se imprimiría distinto según el papel en
 * el que saliera.
 */
export const receiptInstitutionSchema = z.object({
  name: z.string(),
  legalName: z.string().nullable(),
  taxId: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
});

export const receiptStudentSchema = z.object({
  code: z.string(),
  fullName: z.string(),
  gradeName: z.string(),
  sectionName: z.string(),
  cycleName: z.string(),
  /** `true` cuando el estudiante no tiene grado/sección en el ciclo del cobro. */
  assignmentPending: z.boolean().default(false),
});

export const receiptPaymentSchema = z.object({
  id: idSchema,
  receiptNumber: z.string().nullable(),
  status: paymentStatusSchema,
  method: paymentMethodSchema,
  reference: z.string().nullable(),
  effectiveAt: instantSchema,
  amount: moneySchema,
  creditAmount: moneySchema,
  balanceBefore: moneySchema,
  balanceAfter: moneySchema,
  note: z.string().nullable(),
});

export const receiptLineSchema = z.object({
  conceptName: z.string(),
  /** Ciclo de la línea. Un mismo recibo puede tener líneas de ciclos distintos. */
  cycleName: z.string().nullable().default(null),
  /** «Marzo 2026» si es una mensualidad; `null` en conceptos sueltos. */
  monthLabel: z.string().nullable().default(null),
  amount: moneySchema,
  /** Parte de la línea que cubrió mora. */
  lateFee: moneySchema.nullable().default(null),
  /** Parte de la línea que cubrió capital. */
  capital: moneySchema.nullable().default(null),
  /**
   * Lo que el colegio rebajó de ESTA línea, si hubo descuento.
   *
   * La línea conserva el precio de la mensualidad y la rebaja va aparte: el papá
   * tiene que poder ver que su mensualidad vale Q450 y que se le descontaron
   * Q31.50, no un Q418.50 suelto que no cuadra con la lista de precios.
   */
  discount: moneySchema.nullable().default(null),
  balanceBefore: moneySchema,
  balanceAfter: moneySchema,
});

export const receiptDataSchema = z.object({
  templateVersion: z.literal("1"),
  institution: receiptInstitutionSchema,
  student: receiptStudentSchema,
  payment: receiptPaymentSchema,
  lines: z.array(receiptLineSchema),
  /**
   * Total rebajado y por qué, para el pie del comprobante.
   *
   * Va aparte de las líneas porque el papá lee el descuento como un solo número
   * —«me rebajaron Q315»— aunque por dentro esté repartido mes a mes.
   */
  discount: z
    .object({ amount: moneySchema, reason: z.string() })
    .nullable()
    .default(null),
  cashier: z.object({ userName: z.string() }),
  timeZone: z.string(),
  /**
   * Fecha con la que se EMITE el comprobante, distinta de la del pago.
   *
   * El recibo del colegio lleva las dos: «Fecha» (cuándo se extiende el papel) y
   * «Fecha de Pago» (cuándo entró el dinero). Suelen coincidir, pero no siempre:
   * un depósito del viernes puede entregarse el lunes. Si falta, se usa la del
   * pago, que es el comportamiento anterior.
   */
  issuedAt: operationalDateSchema.optional(),
  /**
   * Fecha que se IMPRIME como «Fecha de Pago».
   *
   * Es solo presentación: no altera la mora ni la fecha operativa del pago, que
   * siguen siendo las del registro real. Existe porque la institución fecha el
   * comprobante con el día en que recibió el dinero, aunque lo capture después.
   * Si falta, se imprime la fecha efectiva del pago.
   */
  paidOn: operationalDateSchema.optional(),
});
export type ReceiptData = z.infer<typeof receiptDataSchema>;

/**
 * Tope de pagos en un comprobante consolidado.
 *
 * No es una limitación técnica sino de lectura: pasado esto el papel deja de ser
 * un comprobante que alguien revisa en el mostrador y se vuelve un listado.
 */
export const MAX_CONSOLIDATED_PAYMENTS = 20;

/**
 * Comprobante que agrupa VARIOS pagos en un solo papel.
 *
 * Nace de un caso corriente en la ventanilla: alguien paga por tres estudiantes de
 * una vez y quiere un comprobante, no tres. Los alumnos NO tienen por qué ser
 * familia —un patrocinador, un grupo becado, hermanos con apellidos distintos—,
 * así que no se agrupa por parentesco sino por quien los junte en el mostrador.
 *
 * Cada entrada es un pago REAL e independiente, con su propio consecutivo y su
 * propio efecto sobre el saldo del estudiante. El consolidado es únicamente una
 * presentación: no existe un pago que pertenezca a varios alumnos, porque eso
 * rompería el libro por estudiante y con él saber quién debe qué.
 */
export const consolidatedReceiptDataSchema = z.object({
  templateVersion: z.literal("1"),
  institution: receiptInstitutionSchema,
  entries: z
    .array(
      z.object({
        student: receiptStudentSchema,
        payment: receiptPaymentSchema,
        lines: z.array(receiptLineSchema),
        /**
         * Rebaja de ESTE alumno. Va por entrada y no una sola para el papel
         * entero porque cada hermano tiene su propio pago y su propio saldo: un
         * total suelto al pie no diria a quien se le rebajo qué.
         */
        discount: z
          .object({ amount: moneySchema, reason: z.string() })
          .nullable()
          .default(null),
      }),
    )
    .min(1)
    .max(MAX_CONSOLIDATED_PAYMENTS),
  cashier: z.object({ userName: z.string() }),
  timeZone: z.string(),
  /** Igual que en el recibo individual: cuándo se extiende el papel. */
  issuedAt: operationalDateSchema.optional(),
  /** Igual que en el recibo individual: cuándo entró el dinero. Solo presentación. */
  paidOn: operationalDateSchema.optional(),
});
export type ConsolidatedReceiptData = z.infer<
  typeof consolidatedReceiptDataSchema
>;

export const reprintRequestSchema = z.object({
  reprintId: idSchema,
  reason: z.string().trim().max(200).nullable().default(null),
  installationId: idSchema,
});
